import { createParentComponent } from "@/common/vueUtils";

export type Variant = "primary" | "primary-alt" | "secondary" | "secondary-alt" | "danger" | "danger-alt" | "success" | "link" | "link-slim" | "placeholder" | "action-button";
export type Size = "sm" | "lg";
type Type = "button" | "submit" | "reset";
type Target = "_self" | "_blank" | "_parent" | "_top";
type ComputedLink = { kind: "other"; href: string } | { kind: "none" };
type GaButtonProps = {
  variant?: Variant;
  size?: Size;
  type?: Type;
  href?: string;
  target?: Target;
  disabled?: boolean;
  active?: boolean;
  title?: string;
  id?: string;
  loading?: boolean;
  onClick?: (e: Event) => void;
};

function isDisabled(variant?: Variant, disabled?: boolean, loading?: boolean): boolean {
  return disabled === true || variant === "placeholder" || loading === true;
}

export function getComputedClasses(variant?: Variant, size?: Size, disabled?: boolean, active?: boolean, loading?: boolean): string {
  const getVariant = (): string => {
    if (variant === "secondary") {
      return "btn-secondary";
    }
    if (size === "sm" && variant === "primary-alt") {
      return "btn-alt-primary";
    }
    if (variant === "primary-alt") {
      return "btn-outline-primary";
    }
    if (size === "sm" && variant === "secondary-alt") {
      return "btn-alt-secondary";
    }
    if (variant === "secondary-alt") {
      return "btn-outline-secondary";
    }
    if (variant === "danger") {
      return "btn-danger";
    }
    if (size === "sm" && variant === "danger-alt") {
      return "btn-alt-danger";
    }
    if (variant === "danger-alt") {
      return "btn-outline-danger";
    }
    if (variant === "success") {
      return "btn-success";
    }
    if (variant === "link") {
      return "btn-link";
    }
    if (variant === "link-slim") {
      return "btn-link p-0 text-start";
    }
    if (variant === "placeholder") {
      return "placeholder";
    }
    if (variant === "action-button") {
      return "action-button";
    }
    return "btn-primary";
  };

  const getSize = (): string => {
    if (size === "sm" || size === "lg") {
      return `btn-${size}`;
    }
    return "";
  };

  const getDisabled = (): string => (isDisabled(variant, disabled, loading) ? "disabled" : "");
  const getActive = (): string => (active === true ? "active" : "");
  const getLoading = (): string => (loading === true ? "loading" : "");

  return ["btn", getVariant(), getSize(), getDisabled(), getActive(), getLoading()].join(" ");
}

export const GaButton = createParentComponent<GaButtonProps>((props) => {
  const computedLink: ComputedLink = props.href === undefined ? { kind: "none" } : { kind: "other", href: props.href };
  const computedClass = (): string => getComputedClasses(props.variant, props.size, props.disabled, props.active, props.loading);
  const disabled = isDisabled(props.variant, props.disabled, props.loading);

  switch (computedLink.kind) {
    case "none":
      return (
        <button class={computedClass()} disabled={disabled} type={props.type} tabindex="0" title={props.title} id={props.id} onClick={props.onClick}>
          {props.loading === true ? <span class="spinner-border spinner-border-sm" aria-hidden="true"></span> : null}
          <span class="btn-label">{props.children}</span>
        </button>
      );
    case "other":
      return (
        <a href={computedLink.href} class={computedClass()} aria-disabled={disabled} tabindex={disabled ? -1 : undefined} title={props.title} id={props.id} target={props.target} onClick={props.onClick}>
          {props.loading === true ? <span class="spinner-border spinner-border-sm" aria-hidden="true"></span> : null}
          <span class="btn-label">{props.children}</span>
        </a>
      );
  }
});
